import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import { replaceAdviserVariables } from "utils/replaceAdviserVariables";
import { processSignaturePlaceholders } from "utils/signatureUtils";
import fdLogo from "../assets/images/logo-fisher-dugmore-financial.png";
import "./DocumentPreview.css";

const base_url = process.env.REACT_APP_PDF_SERVER || "http://localhost:8001";

// Constants - use UPPER_CASE for constants
const DEFAULT_ZOOM = 1;
const MAX_ZOOM = 3;
const MIN_ZOOM = 0.5;
const ZOOM_INCREMENT = 0.25;

/**
 * DocumentPreview component for displaying and exporting document content.
 *
 * @param {Object} props - Component props
 * @param {Object} props.documentData - Document data containing HTML content
 * @param {Object} [props.adviserDetails] - Adviser details for variable replacement
 * @param {string} [props.templateName] - Template name for PDF filename
 * @param {string} [props.clientName] - Client name for PDF filename
 */
export const DocumentPreview = ({
  documentData,
  adviserDetails,
  templateName,
  clientName,
}) => {
  const containerRef = useRef();
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Apply page break rules to HTML elements
   * @param {HTMLElement} element - Element to apply rules to
   */
  const applyPageBreakRules = useCallback((element) => {
    const tagName = element.tagName.toLowerCase();

    if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
      element.style.pageBreakAfter = "avoid";
      element.style.pageBreakBefore = "auto";
      element.style.pageBreakInside = "avoid";
    } else if (["table", "p", "li"].includes(tagName)) {
      element.style.pageBreakInside = "avoid";
      element.style.pageBreakAfter = "auto";
      element.style.pageBreakBefore = "auto";
    } else if (element.classList.contains("pdf-signatures")) {
      element.style.pageBreakInside = "avoid";
      element.style.pageBreakAfter = "auto";
      element.style.pageBreakBefore = "auto";
    } else {
      element.style.pageBreakAfter = "auto";
      element.style.pageBreakBefore = "auto";
      element.style.pageBreakInside = "auto";
    }
  }, []);

  /**
   * Remove page number elements from HTML
   * @param {HTMLElement} element - Element to check for page numbers
   */
  const removePageNumbers = useCallback((element) => {
    const text = element.textContent.trim();
    if (text.match(/page\s+\d+\s+of\s+\d+/i) || text.match(/page\s+\d+/i)) {
      element.remove();
    }
  }, []);

  /**
   * Process HTML and add logo before the first h1
   * @param {string} html - HTML content to process
   * @returns {string} Processed HTML content
   */
  const addLogoToHtml = useCallback(
    (html) => {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Apply page break rules to all elements
      const allElements = tempDiv.querySelectorAll("*");
      allElements.forEach(applyPageBreakRules);

      // Remove page number elements
      const pageNumberElements = tempDiv.querySelectorAll("*");
      pageNumberElements.forEach(removePageNumbers);

      // Remove hr elements
      const hrElements = tempDiv.querySelectorAll("hr");
      hrElements.forEach((hr) => hr.remove());

      // Add logo before first h1
      const firstH1 = tempDiv.querySelector("h1");
      if (firstH1) {
        const logoDiv = document.createElement("div");
        logoDiv.className = "document-header";
        logoDiv.innerHTML = `<img src="${fdLogo}" alt="Fisher Dugmore Financial Logo" class="document-logo" />`;
        firstH1.parentNode.insertBefore(logoDiv, firstH1);
      }

      return tempDiv.innerHTML;
    },
    [applyPageBreakRules, removePageNumbers]
  );

  const processedHtml = useMemo(() => {
    const htmlWithAdviserVars = replaceAdviserVariables(
      documentData.html,
      adviserDetails
    );
    // Process signature placeholders with client details
    const htmlWithSignatures = processSignaturePlaceholders(
      htmlWithAdviserVars,
      { full_name: clientName },
      adviserDetails
    );
    return addLogoToHtml(htmlWithSignatures);
  }, [documentData.html, adviserDetails, clientName, addLogoToHtml]);

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel + ZOOM_INCREMENT, MAX_ZOOM);
    setZoomLevel(newZoom);
    window.currentZoomLevel = newZoom;
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - ZOOM_INCREMENT, MIN_ZOOM);
    setZoomLevel(newZoom);
    window.currentZoomLevel = newZoom;
  }, [zoomLevel]);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM);
    window.currentZoomLevel = DEFAULT_ZOOM;
  }, []);

  /**
   * Sanitize filename for PDF export
   * @param {string} filename - Filename to sanitize
   * @returns {string} Sanitized filename
   */
  const sanitizeFilename = useCallback((filename) => {
    if (!filename) return "document";
    return filename
      .replace(/[^a-zA-Z0-9\s\-_]/g, "_")
      .replace(/\s+/g, "_")
      .trim();
  }, []);

  /**
   * Generate PDF filename with template name and client name
   * @returns {string} Generated filename
   */
  const generatePdfFilename = useCallback(() => {
    const sanitizedTemplateName = sanitizeFilename(templateName);
    const sanitizedClientName = sanitizeFilename(clientName);

    if (sanitizedClientName && sanitizedClientName !== "document") {
      return `${sanitizedTemplateName}_${sanitizedClientName}`;
    }
    return sanitizedTemplateName;
  }, [templateName, clientName, sanitizeFilename]);

  const handleExportPdf = useCallback(async () => {
    if (!containerRef.current) return;
    
    // Prevent multiple simultaneous exports
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    const contentContainer =
      containerRef.current.querySelector(".pdf-document");
    if (!contentContainer) {
      console.error("Content container not found");
      setIsExporting(false);
      return;
    }

    // Clone container to avoid changing live DOM
    const clonedContainer = contentContainer.cloneNode(true);

    clonedContainer
      .querySelectorAll("input, textarea, select")
      .forEach((el) => {
        const span = document.createElement("span");

        if (el.tagName === "TEXTAREA") {
          span.textContent = el.value || "\u00A0";
        } else if (el.tagName === "SELECT") {
          span.textContent = el.options[el.selectedIndex]?.text || "\u00A0";
        } else if (el.type === "checkbox") {
          span.textContent = el.checked ? "☑" : "☐";
        } else if (el.type === "radio") {
          span.textContent = el.checked ? "●" : "○";
        } else {
          span.textContent = el.value || "_________________";
        }

        el.replaceWith(span);
      });

    const html = clonedContainer.outerHTML;

    // Generate PDF filename
    const filename = generatePdfFilename();

    try {
      const response = await fetch(`${base_url}/api/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html, filename }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`PDF generation failed with status ${response.status}:`, errorText);
        throw new Error(`PDF generation failed: ${response.status} - ${errorText}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error("PDF generation returned empty file");
      }

      const url = URL.createObjectURL(blob);

      // 👉 Instead of forcing download, just open preview in new tab
      const newWindow = window.open(url, "_blank");
      
      // Clean up the object URL after a delay to allow the window to load
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000); // 10 seconds should be enough for the PDF to load
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert(`PDF generation failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [generatePdfFilename, isExporting]);

  // Expose zoom functions and export state globally for modal header access
  useEffect(() => {
    // Store references for cleanup
    const globalFunctions = {
      handleZoomIn,
      handleZoomOut,
      handleZoomReset,
      handleExportPdf,
      isExporting: () => isExporting,
      currentZoomLevel: zoomLevel
    };

    // Assign to window
    Object.assign(window, globalFunctions);

    return () => {
      // Clean up global references
      Object.keys(globalFunctions).forEach(key => {
        try {
          delete window[key];
        } catch (error) {
          console.warn(`Failed to delete window.${key}:`, error);
        }
      });
    };
  }, [
    zoomLevel,
    isExporting,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleExportPdf,
  ]);



  // Cleanup document after component mounts
  useEffect(() => {
    if (!containerRef.current) return;

    const cleanupDocument = () => {
      const document = containerRef.current;
      if (!document) return;

      // Remove hr elements
      const hrElements = document.querySelectorAll("hr");
      hrElements.forEach((hr) => hr.remove());

      // Remove border-bottom from headings
      const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
      headings.forEach((heading) => {
        heading.style.borderBottom = "none";
      });

      // Remove page number elements
      const allElements = document.querySelectorAll("*");
      allElements.forEach(removePageNumbers);

      // Apply page break rules
      const pageBreakElements = document.querySelectorAll("*");
      pageBreakElements.forEach(applyPageBreakRules);
    };

    // Run immediately and after a short delay to ensure DOM is ready
    cleanupDocument();
    setTimeout(cleanupDocument, 100);
  }, [processedHtml, applyPageBreakRules, removePageNumbers]);

  return (
    <MDBox
      position="relative"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
    >
      <div
        ref={containerRef}
        style={{
          transform: `scale(${zoomLevel})`,
          transformOrigin: "top center",
          transition: "transform 0.2s ease-in-out",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div className="pdf-document">
          {/* Main Content - Header is now included in the backend HTML */}
          <div className="pdf-content">
            <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
          </div>
        </div>
      </div>
    </MDBox>
  );
};

DocumentPreview.propTypes = {
  documentData: PropTypes.shape({
    html: PropTypes.string.isRequired,
  }).isRequired,
  adviserDetails: PropTypes.object,
  templateName: PropTypes.string,
  clientName: PropTypes.string,
};

DocumentPreview.defaultProps = {
  adviserDetails: null,
  templateName: "document",
  clientName: "",
};
